import React, { useMemo, useState } from 'react';
import { CheckIcon, MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { matchesSearch } from '../../utils/search';

/** Amire a választónak szüksége van - a különböző Player típusok mind megfelelnek neki. */
export interface PickablePlayer {
  id: number;
  name: string;
  ageGroup?: string | null;
  ageGroups?: string[] | null;
  ifaCode?: string | null;
}

interface PlayerPickerProps<T extends PickablePlayer> {
  players: T[];
  selectedIds: number[];
  /** Az új kiválasztás. Egyes választásnál legfeljebb egy elemű (üres = nincs kiválasztva). */
  onChange: (ids: number[]) => void;
  /** 'single': legfeljebb egy játékos (pl. saját játékosprofil). */
  mode?: 'single' | 'multi';
  /** Ha a játékos nem választható, az okát adja vissza (a sorban megjelenik). */
  disabledReason?: (player: T) => string | null;
  placeholder?: string;
  id?: string;
}

/** Ennyi találat jelenik meg; ennél többnél a keresés szűkítését kérjük. */
const MAX_RESULTS = 30;

const ageGroupLabel = (p: PickablePlayer) => (p.ageGroups?.length ? p.ageGroups.join(', ') : p.ageGroup ?? '');

/**
 * Kereshető játékosválasztó nagy létszámra: 800 elemes legördülő vagy
 * jelölőnégyzet-lista helyett gépeléssel szűkít, a kiválasztottak pedig
 * felül, címkeként mindig látszanak.
 */
function PlayerPicker<T extends PickablePlayer>({
  players,
  selectedIds,
  onChange,
  mode = 'multi',
  disabledReason,
  placeholder = 'Kezdd el gépelni a játékos nevét...',
  id,
}: PlayerPickerProps<T>) {
  const [search, setSearch] = useState('');
  const selected = useMemo(() => new Set(selectedIds), [selectedIds]);
  const byId = useMemo(() => new Map(players.map((p) => [p.id, p])), [players]);

  const matches = useMemo(() => {
    if (!search.trim()) return [];
    return players
      .filter((p) => matchesSearch(search, p.name, ageGroupLabel(p), p.ifaCode))
      .sort((a, b) => a.name.localeCompare(b.name, 'hu'));
  }, [players, search]);

  const toggle = (playerId: number) => {
    if (mode === 'single') {
      onChange(selected.has(playerId) ? [] : [playerId]);
      setSearch('');
      return;
    }
    onChange(selected.has(playerId) ? selectedIds.filter((x) => x !== playerId) : [...selectedIds, playerId]);
  };

  return (
    <div className="space-y-2">
      {selectedIds.length > 0 && (
        <ul className="flex flex-wrap gap-1.5" aria-label="Kiválasztott játékosok">
          {selectedIds.map((pid) => {
            const p = byId.get(pid);
            return (
              <li
                key={pid}
                className="inline-flex items-center gap-1 rounded-full bg-primary-50 py-1 pl-2.5 pr-1 text-sm text-primary-800 ring-1 ring-primary-200"
              >
                {p ? `${p.name} (${ageGroupLabel(p)})` : `#${pid}`}
                <button
                  type="button"
                  onClick={() => toggle(pid)}
                  aria-label={`${p?.name ?? 'Játékos'} eltávolítása`}
                  className="flex h-5 w-5 items-center justify-center rounded-full hover:bg-primary-100"
                >
                  <XMarkIcon className="h-3.5 w-3.5" />
                </button>
              </li>
            );
          })}
        </ul>
      )}

      <div className="relative">
        <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
        <input
          id={id}
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={placeholder}
          autoComplete="off"
          className="block w-full rounded-md border-gray-300 pl-9 text-sm focus:border-primary-500 focus:ring-primary-500"
        />
      </div>

      {search.trim() && (
        <div className="max-h-56 overflow-y-auto rounded-md border border-gray-200">
          {matches.length === 0 ? (
            <p className="px-3 py-2 text-sm text-gray-500">Nincs ilyen nevű játékos.</p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {matches.slice(0, MAX_RESULTS).map((p) => {
                const isSelected = selected.has(p.id);
                const reason = !isSelected ? disabledReason?.(p) ?? null : null;
                return (
                  <li key={p.id}>
                    <button
                      type="button"
                      onClick={() => toggle(p.id)}
                      disabled={reason !== null}
                      className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm ${
                        isSelected ? 'bg-primary-50' : 'hover:bg-gray-50'
                      } disabled:cursor-not-allowed disabled:opacity-50`}
                    >
                      <span
                        className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                          isSelected ? 'border-primary-600 bg-primary-600 text-white' : 'border-gray-300'
                        }`}
                      >
                        {isSelected && <CheckIcon className="h-3 w-3" />}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="font-medium text-gray-900">{p.name}</span>{' '}
                        <span className="text-gray-500">({ageGroupLabel(p)})</span>
                        {reason && <span className="block text-xs text-gray-500">{reason}</span>}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          {matches.length > MAX_RESULTS && (
            <p className="border-t border-gray-100 px-3 py-2 text-xs text-gray-500">
              {matches.length - MAX_RESULTS} további találat - gépelj tovább a szűkítéshez.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export default PlayerPicker;
